import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type { ListResponse, Role, RoleGrant, RoleHistoryEvent, RolePermission, RoleRecordRules, Translated, TranslatedDescription } from '@/types/api'
import type { RequestOptions } from '../crud'

/** Roles are company-wide permission bundles. */

export type RoleInput = Translated &
  TranslatedDescription & {
    code: string
    status: string
  }

/**
 * A new role belongs to one client type, fixed on create: `base_role_id`
 * («На основе роли») takes that role's client type and server behaviour
 * (not its grants), else `client_type_id` gives a role that behaves as
 * itself. Send exactly one of them.
 */
export type RoleCreateInput = RoleInput & ({ base_role_id: string; client_type_id?: never } | { client_type_id: string; base_role_id?: never })

export type RoleCopyInput = Translated & Partial<TranslatedDescription> & { code: string }

const key = (id: string) => `/roles/${encodeURIComponent(id)}`

export const rolesService = {
  path: 'roles',

  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<Role>>('/roles', { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },

  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<Role>(key(id), options)
    return data
  },

  async create(body: RoleCreateInput) {
    const { data } = await http.post<Role>('/roles', body)
    return data
  },

  /**
   * A new company role with the source role's grants. The key makes a
   * retried request return the first copy instead of making a second one.
   */
  async copy(id: string, body: RoleCopyInput, idempotencyKey: string) {
    const { data } = await http.post<Role>(`${key(id)}/copy`, body, { headers: { 'Idempotency-Key': idempotencyKey } })
    return data
  },

  async update(id: string, body: Partial<RoleInput>) {
    const { data } = await http.put<Role>(key(id), body)
    return data
  },

  async remove(id: string) {
    await http.delete(key(id))
  },

  /** One reviewed replacement of the complete grant set. */
  async replacePermissions(id: string, grants: RoleGrant[]) {
    const { data } = await http.put<RolePermission[]>(`${key(id)}/permissions`, {
      grants: grants.map((grant) => ({ ...grant, conditions: grant.conditions ?? {} })),
    })
    return data ?? []
  },

  /**
   * Saves the access tree at once: the ticked rows and the role's plain grants.
   * The server adds the visible rows' picker reads and keeps special grants.
   */
  async replaceAccess(id: string, menuCodes: string[], grants: RoleGrant[], records: RoleRecordRules[]) {
    const { data } = await http.put<{ menu_codes: string[]; grants: RolePermission[]; records: RoleRecordRules[] }>(`${key(id)}/access`, {
      menu_codes: menuCodes,
      grants: grants.map((grant) => ({ ...grant, conditions: grant.conditions ?? {} })),
      records,
    })
    return { menu_codes: data.menu_codes ?? [], grants: data.grants ?? [], records: data.records ?? [] }
  },

  /** The role's «Записи» rules, one per table. */
  async records(id: string, options?: RequestOptions) {
    const { data } = await http.get<RoleRecordRules[]>(`${key(id)}/records`, options)
    return data ?? []
  },

  /** The menu pages (menu codes) the role ticks; a page also needs its rights to show. */
  async menu(id: string, options?: RequestOptions) {
    const { data } = await http.get<{ codes: string[] }>(`${key(id)}/menu`, options)
    return data.codes ?? []
  },

  /** Sets the role's ticked menu pages to exactly `codes`. */
  async replaceMenu(id: string, codes: string[]) {
    const { data } = await http.put<{ codes: string[] }>(`${key(id)}/menu`, { codes })
    return data.codes ?? []
  },

  /** Who changed the role's grants, newest first. */
  async history(id: string, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<RoleHistoryEvent>>(`${key(id)}/history`, { params: { limit: 200 }, ...options })
    return data.items ?? []
  },
}
